import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import { relinkErrorOf, type RelinkError } from './relinkErrors'
import { toRelink, type Relink } from './relinkWire'
import { AggregatorError } from './useAggregator'

/** The most ids the server takes per list; more is a 422 before any work. */
export const RESOLVE_BATCH = 50

/** What the user answered for one old account. */
export type Answer =
  | { kind: 'pair'; oldId: string; newId: string }
  | { kind: 'keep'; oldId: string }

/** A resolve that stopped on a documented 4xx. `error` is what the screen switches on. */
export class RelinkResolveError extends Error {
  readonly error: RelinkError
  /** True when an earlier batch was already saved: those merges are permanent. */
  readonly partial: boolean
  /** True when a POST got no answer at all (a network failure): the server may have
   *  committed that batch and only the response was lost, so the outcome is unknown. */
  readonly unconfirmed: boolean
  constructor(error: RelinkError, partial = false, unconfirmed = false) {
    super(`Relink resolve failed: ${error.kind}`)
    this.name = 'RelinkResolveError'
    this.error = error
    this.partial = partial
    this.unconfirmed = unconfirmed
  }
}

const STILL_ACTIVE_MESSAGE = 'This reconnect is no longer active.'
const POLL_MS = 5000

async function getRelink(itemId: string): Promise<Relink> {
  const { data, error, response } = await api.GET('/api/v1/aggregator/items/{item_id}/relink', {
    params: { path: { item_id: itemId } },
  })
  if (error !== undefined || data === undefined) throw new AggregatorError(error, response.status)
  return toRelink(data)
}

/**
 * The match state of one reconnect, `relink-state` (a `live` surface: the wire
 * carries no sync date to be stale against). Polls every 5 s WHILE `pending` —
 * the server keeps pairing accounts on later sync pages, and `needs_review` is
 * not final until the first sync completes — and on window focus. Once the
 * status leaves `pending` the polling stops.
 */
export function useRelinkState(itemId: string): AsyncState<Relink> {
  const navigate = useNavigate()
  const query = useQuery({
    queryKey: keys.aggregator.relink(itemId),
    queryFn: () => getRelink(itemId),
    refetchInterval: (q) => (q.state.data?.status === 'pending' ? POLL_MS : false),
    refetchOnWindowFocus: true,
    staleTime: 0,
    // A 404 is an answer (not a reconnect, or no longer live), not a blip.
    retry: (count, err) => !(err instanceof AggregatorError && err.status === 404) && count < 2,
  })
  const notFound = query.error instanceof AggregatorError && query.error.status === 404
  return fromQuery(query, {
    message: () => (notFound ? STILL_ACTIVE_MESSAGE : 'The reconnect could not be loaded.'),
    recovery: notFound
      ? { label: 'Back to connections', onAct: () => navigate('/connections') }
      : { label: 'Try again', onAct: () => void query.refetch() },
  })
}

/** Drops every answer whose old (or, for a pair, new) account is no longer unmatched. */
export function pruneAnswers(relink: Relink, answers: Answer[]): { kept: Answer[]; dropped: Answer[] } {
  const oldIds = new Set(relink.unmatchedOld.map((a) => a.id))
  const newIds = new Set(relink.unmatchedNew.map((a) => a.id))
  const kept: Answer[] = []
  const dropped: Answer[] = []
  for (const a of answers) {
    const open = oldIds.has(a.oldId) && (a.kind === 'keep' || newIds.has(a.newId))
    ;(open ? kept : dropped).push(a)
  }
  return { kept, dropped }
}

function batchesOf(answers: Answer[]): Answer[][] {
  const pairs = answers.filter((a) => a.kind === 'pair')
  const keeps = answers.filter((a) => a.kind === 'keep')
  const n = Math.max(Math.ceil(pairs.length / RESOLVE_BATCH), Math.ceil(keeps.length / RESOLVE_BATCH))
  return Array.from({ length: n }, (_, i) => [
    ...pairs.slice(i * RESOLVE_BATCH, (i + 1) * RESOLVE_BATCH),
    ...keeps.slice(i * RESOLVE_BATCH, (i + 1) * RESOLVE_BATCH),
  ])
}

async function postBatch(itemId: string, batch: Answer[]): Promise<Relink> {
  const { data, error, response } = await api.POST('/api/v1/aggregator/items/{item_id}/relink/resolve', {
    params: { path: { item_id: itemId } },
    body: {
      pairs: batch.flatMap((a) => (a.kind === 'pair' ? [{ old_account_id: a.oldId, new_account_id: a.newId }] : [])),
      keep_separate: batch.flatMap((a) => (a.kind === 'keep' ? [a.oldId] : [])),
    },
  })
  if (error !== undefined || data === undefined) throw new AggregatorError(error, response.status)
  return toRelink(data)
}

function refresh(client: QueryClient, itemId: string): Promise<Relink> {
  return client.fetchQuery({ queryKey: keys.aggregator.relink(itemId), queryFn: () => getRelink(itemId), staleTime: 0 })
}

/**
 * Sends the user's answers. Each is checked against the cached match state
 * (kept fresh by polling; a 409 covers the gap) first — the server keeps pairing while `pending`, and an answer about an
 * account that has since been paired must never be sent; it comes back in
 * `dropped` for the screen to say so. Batches of at most 50 per list (a longer
 * list is a 422), sent one after another; each is atomic and a partial answer
 * is fine. A `409 account_changed` / `already_paired` refetches, prunes again
 * and retries ONCE; a second one stops with a `RelinkResolveError`, never a
 * third request. `relink_completed` refetches and returns what is left.
 *
 * **A failure after a batch was saved is `partial`.** Those merges cannot be
 * undone, so the error says so (`RelinkResolveError.partial`) and the accounts
 * and items lists are invalidated in a `finally` — whatever stopped the loop.
 */
export function useResolveRelink(itemId: string): {
  resolve: (answers: Answer[]) => Promise<{ relink: Relink; dropped: Answer[] }>
} {
  const client = useQueryClient()
  return useMemo(
    () => ({
      resolve: async (answers: Answer[]) => {
        const key = keys.aggregator.relink(itemId)
        const dropped: Answer[] = []
        const prune = (relink: Relink, from: Answer[]): Answer[] => {
          const split = pruneAnswers(relink, from)
          dropped.push(...split.dropped)
          return split.kept
        }
        let current = client.getQueryData<Relink>(key) ?? (await refresh(client, itemId))
        let queue = batchesOf(prune(current, answers))
        let retried = false
        let sent = false
        let unconfirmed = false
        try {
          while (queue.length > 0) {
            const [batch, ...rest] = queue
            if (batch === undefined) break
            try {
              current = await postBatch(itemId, batch)
              client.setQueryData(key, current)
              sent = true
              queue = rest
            } catch (cause) {
              if (!(cause instanceof AggregatorError)) {
                unconfirmed = true
                throw cause
              }
              const error = relinkErrorOf(cause.status, cause.body)
              if (error.kind === 'relink_completed') {
                dropped.push(...queue.flat())
                sent = true
                current = await refresh(client, itemId)
                queue = []
              } else if (error.kind === 'account_changed' || error.kind === 'already_paired') {
                if (retried) throw new RelinkResolveError(error)
                retried = true
                current = await refresh(client, itemId)
                queue = batchesOf(prune(current, queue.flat()))
              } else {
                throw new RelinkResolveError(error)
              }
            }
          }
        } catch (thrown) {
          if (!sent && !unconfirmed) throw thrown
          const error: RelinkError = thrown instanceof RelinkResolveError ? thrown.error : { kind: 'other', status: 0 }
          throw new RelinkResolveError(error, sent, unconfirmed)
        } finally {
          // A POST with no answer may have been committed: refetch the truth too.
          if (unconfirmed) void client.invalidateQueries({ queryKey: key })
          if (sent || unconfirmed) {
            // The merged new accounts are deleted server-side and the old ones carry
            // the new item id, so both lists are stale now — even when a later batch failed.
            void client.invalidateQueries({ queryKey: keys.accounts.all() })
            void client.invalidateQueries({ queryKey: keys.aggregator.items() })
          }
        }
        return { relink: current, dropped }
      },
    }),
    [client, itemId],
  )
}
